import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { publishAuthEvent, subscribeAuthEvents, TAB_ID } from "@/lib/auth-channel";

interface User {
  id: number;
  email: string;
  name: string;
  isAdmin: boolean;
  hasPassword: boolean;
  plan: "free" | "pro";
  trialEndsAt: string | null;
  brandingGrandfathered: boolean;
  inGracePeriod: boolean;
  gracePeriodEndsAt: string | null;
  role: "owner" | "field";
  workspaceOwnerId: number | null;
  mustChangePassword: boolean;
  hourlyRate: number | null;
}

export function getTrialInfo(user: User | null): { isOnTrial: boolean; trialDaysLeft: number; hasProAccess: boolean } {
  if (!user) return { isOnTrial: false, trialDaysLeft: 0, hasProAccess: false };
  if (user.plan === "pro") return { isOnTrial: false, trialDaysLeft: 0, hasProAccess: true };
  if (user.trialEndsAt) {
    const diffMs = new Date(user.trialEndsAt).getTime() - Date.now();
    if (diffMs > 0) {
      return { isOnTrial: true, trialDaysLeft: Math.ceil(diffMs / (1000 * 60 * 60 * 24)), hasProAccess: true };
    }
  }
  return { isOnTrial: false, trialDaysLeft: 0, hasProAccess: false };
}

/** Accounts from before the logo and brand color became Pro-only keep them on any plan. */
export function hasBrandingAccess(user: User | null): boolean {
  return !!user?.brandingGrandfathered || getTrialInfo(user).hasProAccess;
}

export interface RegisterResult {
  emailVerificationRequired?: boolean;
  email?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string, returnTo?: string, agreedToTerms?: boolean, timezone?: string, refCode?: string) => Promise<RegisterResult>;
  logout: () => Promise<void>;
  resendVerification: (email: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  /**
   * Called when the client detects the user is no longer signed in
   * (e.g., a background heartbeat returned 401). This shares one code
   * path with the cross-tab "signed-out" handler so local detection
   * and remote-tab events behave identically.
   */
  notifySignedOut: () => void;
}

export const AuthContext = createContext<AuthContextType | null>(null);

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

const PUBLIC_LOGIN_LIKE_PATHS = new Set([
  "/login",
  "/auth",
  "/signup",
  "/forgot-password",
]);

function navigateToLogin() {
  if (typeof window === "undefined") return;
  const target = `${BASE}/login`;
  const path = window.location.pathname;
  // Avoid redundant navigation if we're already on a login-like screen.
  if (PUBLIC_LOGIN_LIKE_PATHS.has(path) || path.startsWith(`${BASE}/login`) || path.startsWith(`${BASE}/auth`)) {
    return;
  }
  window.location.assign(target);
}

async function authFetch(path: string, opts?: RequestInit) {
  const res = await fetch(`${BASE}/api/auth${path}`, {
    ...opts,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...opts?.headers },
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw Object.assign(new Error(data?.error || `Request failed (${res.status})`), { data, status: res.status });
  return data;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  // Track whether the local user state was ever populated. We only treat a
  // `signed-out` event as "navigate to login" if this tab actually believed
  // the user was signed in — otherwise public/landing visitors get bounced
  // to /login by an unrelated tab's heartbeat noise.
  const wasSignedInRef = useRef(false);

  /**
   * Single shared sign-out handler. Used by:
   *  - manual logout (after the server call resolves either way),
   *  - cross-tab "logout" / "signed-out" events from another tab,
   *  - locally-detected sign-outs (e.g. heartbeat 401 in this tab).
   *
   * `broadcast=true` tells other tabs about it. `navigate=true` routes this
   * tab to /login if it actually was signed in. We also flip the
   * wasSignedInRef before clearing user state so a re-entry from a listener
   * doesn't double-broadcast.
   */
  const applySignedOut = useCallback((opts: { broadcast: boolean; navigate: boolean }) => {
    const wasSignedIn = wasSignedInRef.current;
    wasSignedInRef.current = false;
    setUser(null);
    if (opts.broadcast) {
      publishAuthEvent({ type: "signed-out", tabId: TAB_ID });
    }
    if (opts.navigate && wasSignedIn) {
      navigateToLogin();
    }
  }, []);

  const notifySignedOut = useCallback(() => {
    applySignedOut({ broadcast: true, navigate: true });
  }, [applySignedOut]);

  const checkAuth = useCallback(async () => {
    try {
      const data = await authFetch("/me");
      if (data && data.id) {
        setUser(data);
        wasSignedInRef.current = true;
      } else {
        setUser(null);
      }
    } catch {
      // Initial /me failure is silent — treat as signed-out.
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (import.meta.env.DEV) {
      const params = new URLSearchParams(window.location.search);
      const devEmail = params.get("dev_login");
      if (devEmail) {
        authFetch("/dev-login", {
          method: "POST",
          body: JSON.stringify({ email: devEmail }),
        })
          .then((data) => {
            if (data && data.id) {
              const dashboardUrl = new URL(window.location.href);
              dashboardUrl.searchParams.delete("dev_login");
              dashboardUrl.pathname = BASE || "/";
              window.location.replace(dashboardUrl.toString());
            } else {
              setLoading(false);
            }
          })
          .catch(() => {
            setLoading(false);
          });
        return;
      }
    }
    checkAuth();
  }, [checkAuth]);

  // Cross-tab auth sync: react to logout / signed-out / login events from
  // other tabs in the same browser. The channel itself filters out events
  // originating in this tab, so receiving a `logout`/`signed-out` here means
  // another tab told us the session is gone.
  useEffect(() => {
    const unsub = subscribeAuthEvents((event) => {
      if (event.type === "logout" || event.type === "signed-out") {
        // Don't re-broadcast — that's how the event reached us in the first
        // place. Just apply local sign-out and navigate.
        applySignedOut({ broadcast: false, navigate: true });
      } else if (event.type === "login") {
        // Another tab signed in — refresh /me here without bouncing.
        checkAuth();
      }
    });
    return unsub;
  }, [checkAuth, applySignedOut]);

  const login = useCallback(async (email: string, password: string) => {
    const data = await authFetch("/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    setUser(data);
    wasSignedInRef.current = true;
    publishAuthEvent({ type: "login", tabId: TAB_ID });
  }, []);

  const register = useCallback(async (name: string, email: string, password: string, returnTo?: string, agreedToTerms?: boolean, timezone?: string, refCode?: string): Promise<RegisterResult> => {
    const rdt_cid = localStorage.getItem("rdt_cid") ?? undefined;
    const storedRefCode = refCode ?? sessionStorage.getItem("ref_code") ?? undefined;
    const data = await authFetch("/register", {
      method: "POST",
      body: JSON.stringify({ name, email, password, ...(returnTo ? { returnTo } : {}), agreedToTerms: agreedToTerms === true, ...(timezone ? { timezone } : {}), ...(rdt_cid ? { rdt_cid } : {}), ...(storedRefCode ? { refCode: storedRefCode } : {}) }),
    });
    sessionStorage.removeItem("ref_code");
    if (data?.emailVerificationRequired) {
      return { emailVerificationRequired: true, email: data.email };
    }
    setUser(data);
    wasSignedInRef.current = true;
    publishAuthEvent({ type: "login", tabId: TAB_ID });
    return {};
  }, []);

  const logout = useCallback(async () => {
    // Best-effort server logout. If CSRF is missing/invalid, network is down,
    // or the server returns any non-2xx, we still sign the user out locally
    // and route them to /login so they never get stuck signed in.
    try {
      await authFetch("/logout", { method: "POST" });
    } catch {
      // swallow — local sign-out below still happens
    }
    // Use the "logout" event type (rather than the implicit "signed-out"
    // applySignedOut publishes) so listeners can distinguish an explicit
    // user action from a passively-detected expiry. We then run the shared
    // local sign-out flow without a second broadcast.
    publishAuthEvent({ type: "logout", tabId: TAB_ID });
    applySignedOut({ broadcast: false, navigate: true });
  }, [applySignedOut]);

  const resendVerification = useCallback(async (email: string) => {
    await authFetch("/resend-verification", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const data = await authFetch("/me");
      if (data && data.id) {
        setUser(data);
        wasSignedInRef.current = true;
      } else if (data === null) {
        // /me returned a body that wasn't a user — treat as signed out.
        applySignedOut({ broadcast: true, navigate: true });
      }
    } catch (err: unknown) {
      // Background refresh shouldn't surface errors, but a true 401 means
      // the session is gone — apply the same sign-out treatment.
      const status = (err as { status?: number } | null)?.status;
      if (status === 401) {
        applySignedOut({ broadcast: true, navigate: true });
      }
    }
  }, [applySignedOut]);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, resendVerification, refreshUser, notifySignedOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
